/**
 * Margin Report Page
 *
 * Main page for the margin (profitability) report.
 * Supports deep linking via URL params for date range.
 * Uses SSE for real-time progress during report generation.
 * @see US-25d.11 - Page MarginReportPage
 */

import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TrendingUp, AlertTriangle, Loader2 } from 'lucide-react';
import { EmptyState, DatePicker, Button } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { useFormatDate, useCutoffHour } from '@/hooks';
import { useMarginReportWithProgress } from '../hooks/useMarginReport';
import { MarginReportSummary } from '../components/MarginReportSummary';
import { MarginReportTable } from '../components/MarginReportTable';
import { MarginReportFormulasDrawer } from '../components/MarginReportFormulasDrawer';
import { SalesCompletenessNotice } from '@/features/pos-sales/components';

/**
 * Convert DateRange to date/datetime strings for API.
 * Includes time (YYYY-MM-DDTHH:mm) when hours/minutes are non-zero.
 */
function dateRangeToParams(range: DateRange): { dateFrom: string; dateTo: string } | null {
  if (!range.start || !range.end) return null;

  const formatDate = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const datePart = `${year}-${month}-${day}`;
    const hours = d.getHours();
    const minutes = d.getMinutes();
    if (hours === 0 && minutes === 0) return datePart;
    return `${datePart}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  return {
    dateFrom: formatDate(range.start),
    dateTo: formatDate(range.end),
  };
}

/**
 * Parse URL params for deep linking
 */
function parseUrlParams(searchParams: URLSearchParams, cutoffHour: number): {
  dateRange: DateRange;
} {
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');

  // Default to current month if no dates, with cutoff hour
  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1, cutoffHour, 0, 0, 0);
  const defaultEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoffHour, 0, 0, 0);

  return {
    dateRange: {
      start: dateFrom ? new Date(dateFrom) : defaultStart,
      end: dateTo ? new Date(dateTo) : defaultEnd,
    },
  };
}

export default function MarginReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatDateShort, formatDateTime } = useFormatDate();
  const cutoffHour = useCutoffHour();

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams, cutoffHour), [searchParams, cutoffHour]);

  // State for date range (initialized from URL)
  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);

  // State for formulas drawer
  const [formulasDrawerOpen, setFormulasDrawerOpen] = useState(false);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
      const dates = dateRangeToParams(range);
      if (dates) {
        updateUrl({
          dateFrom: dates.dateFrom,
          dateTo: dates.dateTo,
        });
      }
    },
    [updateUrl]
  );

  // Build query params
  const queryParams = useMemo(() => dateRangeToParams(dateRange), [dateRange]);

  // Fetch data with SSE progress
  const {
    generate,
    progress,
    stepLabel,
    data: reportData,
    isLoading,
    error,
  } = useMarginReportWithProgress(queryParams);

  // Determine if we have valid dates
  const hasValidDates = dateRange.start && dateRange.end;

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <TrendingUp className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-dark-500">Rapport de Marge</h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              Analysez la rentabilite de votre activite sur une periode
            </p>
          </div>
        </div>
      </div>

      {/* Date Picker and Generate Button */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <DatePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            showTime
          />
          <Button onClick={generate} disabled={isLoading || !hasValidDates}>
            {isLoading ? 'Chargement...' : 'Generer'}
          </Button>
        </div>

        {/* Progress indicator */}
        {isLoading && progress && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
              <span>{stepLabel}</span>
            </div>
            <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress.progress}%` }}
              />
            </div>
            <div className="text-xs text-neutral-500">{progress.progress}%</div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 bg-neutral-50">
        {!hasValidDates ? (
          <EmptyState
            icon={TrendingUp}
            title="Selectionnez une periode"
            description="Choisissez une date de debut et de fin pour generer le rapport de marge"
          />
        ) : error ? (
          <EmptyState
            icon={AlertTriangle}
            title="Erreur de chargement"
            description={error}
          />
        ) : isLoading && !reportData ? (
          <EmptyState
            icon={TrendingUp}
            title="Generation en cours..."
            description="Le rapport est en cours de generation"
          />
        ) : reportData ? (
          <div className="space-y-6">
            {/* Ventes incompletes sur la periode (tickets en retard ou recus sans detail) */}
            <SalesCompletenessNotice mode="report" dateFrom={queryParams?.dateFrom} dateTo={queryParams?.dateTo} />

            {/* KPIs Summary */}
            <MarginReportSummary report={reportData} />

            {/* Data Table */}
            <MarginReportTable
              report={reportData}
              loading={false}
              onHelpClick={() => setFormulasDrawerOpen(true)}
            />

            {/* Meta info */}
            <div className="text-xs text-neutral-400 text-right pb-4">
              Site: {reportData.meta.brandName} |
              Periode: {formatDateShort(reportData.meta.dateFrom)} -{' '}
              {formatDateShort(reportData.meta.dateTo)} |
              Genere le {formatDateTime(reportData.meta.generatedAt)}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={TrendingUp}
            title="Aucune donnee"
            description="Aucune donnee disponible pour cette periode"
          />
        )}
      </div>

      {/* Formulas Drawer */}
      <MarginReportFormulasDrawer
        open={formulasDrawerOpen}
        onOpenChange={setFormulasDrawerOpen}
      />
    </div>
  );
}
